import { toast, useLocalStorage } from '@buffer-mono/popcorn'
import * as React from 'react'
import { useEffect } from 'react'

import { useUser } from '../../common/context/User'
import { MODALS } from '../../common/types'
import type { Service } from '../../exports/Orchestrator/channelConnections/types'
import { getServiceName } from '../../exports/Orchestrator/channelConnections/utils'
import useChannels from '../Modal/hooks/useChannels'
import { ModalContent } from '../Modal/ModalContent'
import {
  cleanupUrlParams,
  shouldPrefillComposerDataAfterConnection,
  shouldShowChannelConnectionPrompt,
  shouldShowDownloadAppModal,
  shouldShowPaywallModal,
  shouldShowTeamMemberDowngradeModal,
  shouldShowTrialExpiredModal,
  shouldShowTrialSignupWelcomeModal,
  shouldSkipCelebrationModals,
  shouldTriggerChannelOnboardingModalFromUrl,
  shouldTriggerChannelRefreshModalFromUrl,
  shouldTriggerSuccessModalFromUrl,
} from '../Modal/utils'
import { useModalManager } from './hooks/useModalManager'
import {
  handleChannelConnectionOnboardingViaURL,
  handleChannelRefreshViaURL,
} from './ModalManagerUtils'
import useDismissBanner, {
  BannerTypes,
} from '../../common/hooks/useDismissBanner'
import { createPortal } from 'react-dom'
import usePortalContainer from '@buffer-mono/popcorn/src/helpers/usePortalContainer'

const ModalManager = (): JSX.Element => {
  const { openModal } = useModalManager()
  const [promoteDownloadAppModal] = useLocalStorage(
    'promoteDownloadAppModal',
    true,
  )

  const { isActive: isChannelGroupsPromptActive } = useDismissBanner({
    banner: BannerTypes.channelGroupsAnnouncement,
  })

  const shouldPromoteDownloadAppModal =
    promoteDownloadAppModal && shouldShowDownloadAppModal()

  const user = useUser()
  const { connectedChannels = [], connectedChannelIds = [] } = useChannels()

  const shouldShowChannelGroupsPrompt = connectedChannelIds.length >= 3

  useEffect(() => {
    if (!user?.id) return

    /* This modal must have the highest priority to be shown up to mobile users and avoid
     they see another modal which they cannot interact with */
    if (shouldPromoteDownloadAppModal) {
      openModal({
        key: MODALS.downloadApp,
      })
    }

    // Show Team Member Downgrade Modal
    if (shouldShowTeamMemberDowngradeModal(user)) {
      openModal({
        key: MODALS.teamMemberDowngrade,
      })
    }

    // Show Trial Expired Modal
    if (shouldShowTrialExpiredModal(user)) {
      openModal({
        key: MODALS.trialExpired,
        data: {
          ctaButton: 'noclick',
          upgradePathName: 'noclickTrialExpiredModal-upgrade',
          ctaView: 'trialExpired',
          ctaLocation: ' noclick',
        },
      })
    }

    if (shouldTriggerChannelOnboardingModalFromUrl()) {
      handleChannelConnectionOnboardingViaURL({ openModal })
    }

    if (shouldTriggerChannelRefreshModalFromUrl()) {
      handleChannelRefreshViaURL({ openModal })
    }

    if (shouldTriggerSuccessModalFromUrl()) {
      openModal({
        key: MODALS.success,
        data: {
          ctaButton: 'noclick',
          upgradePathName: 'noclickCheckoutSuccess-upgrade',
          ctaView: 'successModal',
          ctaLocation: ' noclickCheckoutSuccess',
          selectedPlan:
            user.currentOrganization?.billing?.subscription?.plan?.id,
        },
      })

      cleanupUrlParams()
    }

    // Show Channel Connection prompt
    if (shouldShowChannelConnectionPrompt(user)) {
      openModal({
        key: MODALS.channelConnectionPrompt,
      })
    }

    // Show Trial Signup Welcome Modal
    if (shouldShowTrialSignupWelcomeModal(user)) {
      openModal({
        key: MODALS.trialSignupWelcomeModal,
      })
    }

    // Show Paywall Modal
    if (shouldShowPaywallModal(user)) {
      openModal({
        key: MODALS.paywall,
      })
    }
  }, [user.currentOrganization?.id])

  useEffect(() => {
    if (shouldShowChannelGroupsPrompt && isChannelGroupsPromptActive) {
      openModal({
        key: MODALS.channelGroupsPrompt,
      })
    }
  }, [shouldShowChannelGroupsPrompt, isChannelGroupsPromptActive, openModal])

  useEffect(() => {
    // Open Composer with Prefilled Data
    if (
      connectedChannels &&
      connectedChannels.length > 0 &&
      shouldPrefillComposerDataAfterConnection()
    ) {
      if (window.__openComposer) {
        const cta = 'connectChannelOnboarding-previewMode'
        window.__openComposer(
          {
            channels: connectedChannelIds,
            cta: `publish-modal-${cta}-1`,
          },
          {
            prefillFromLocalStorage: true,
          },
        )
        if (shouldSkipCelebrationModals()) {
          const service = connectedChannels[0].service
          toast.success(
            `Great! your ${getServiceName(service as Service)} channel${
              connectedChannelIds.length > 1 ? 's have' : ' has'
            } been Connected 🎉`,
          )
        }
        cleanupUrlParams()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectedChannels, window.__openComposer])

  const container = usePortalContainer()

  if (!user?.id) return <></>

  return createPortal(<ModalContent />, container ?? document.body)
}

const ModalManagerMemo = React.memo(ModalManager)

export { ModalManagerMemo as ModalManager }
export default ModalManagerMemo
